/**
 * Fog of War leve baseado em grid (células), pensado para mobile. Revela células ao
 * redor do player conforme explora; persiste no save. Sem precisão por pixel.
 */
import { GameConfig } from "../config/GameConfig";

export class FogOfWar {
  private readonly cell = GameConfig.fog.cellSize;
  /** 10.9HQ — extensão do grid: cada REGIÃO tem a sua (arenaHalfSize + folga). Antes era fixa em
   *  112 m e as áreas têm 220/260/310 m de raio: fora dos 112 m nada era revelado — o minimapa
   *  ficava "sem lugares" (Thiago). */
  private readonly extent: number;
  readonly cols: number;
  readonly rows: number;
  private readonly revealed: Uint8Array;

  constructor(extent: number = GameConfig.fog.worldExtent) {
    this.extent = extent;
    this.cols = Math.ceil((this.extent * 2) / this.cell);
    this.rows = this.cols;
    this.revealed = new Uint8Array(this.cols * this.rows);
  }

  /** 10.5M — assinatura do GRID. O save guarda índices achatados (r*cols+c); se o mundo
   *  muda de tamanho/célula, os índices antigos apontam para lugares errados (mancha
   *  diagonal). Comparando a assinatura, o fog antigo é descartado em vez de corromper. */
  static signature(): string {
    return `${GameConfig.fog.cellSize}xR`; // 10.9HQ — "R" = grid por região (o save é migrado do grid antigo)
  }
  /** 10.9HQ — assinatura do grid antigo (fixo em worldExtent): o save com ela é MIGRADO, não descartado. */
  static readonly LEGACY_SIGNATURE = `${GameConfig.fog.cellSize}x${GameConfig.fog.worldExtent}`;
  /** Extensão (metros) deste grid. */
  get worldExtent(): number { return this.extent; }

  private col(x: number): number {
    return Math.floor((x + this.extent) / this.cell);
  }
  private row(z: number): number {
    return Math.floor((z + this.extent) / this.cell);
  }
  private idx(c: number, r: number): number {
    return r * this.cols + c;
  }
  private inBounds(c: number, r: number): boolean {
    return c >= 0 && c < this.cols && r >= 0 && r < this.rows;
  }

  isRevealedWorld(x: number, z: number): boolean {
    const c = this.col(x);
    const r = this.row(z);
    return this.inBounds(c, r) ? this.revealed[this.idx(c, r)] === 1 : false;
  }
  isRevealedCell(c: number, r: number): boolean {
    return this.inBounds(c, r) ? this.revealed[this.idx(c, r)] === 1 : false;
  }
  /** Centro (mundo) de uma célula. */
  cellCenter(c: number, r: number, out: { x: number; z: number }): void {
    out.x = -this.extent + (c + 0.5) * this.cell;
    out.z = -this.extent + (r + 0.5) * this.cell;
  }

  /** Revela um disco ao redor de (x,z). Retorna true se algo novo foi revelado. */
  reveal(x: number, z: number): boolean {
    const rad = GameConfig.fog.revealRadius;
    const c0 = this.col(x - rad);
    const c1 = this.col(x + rad);
    const r0 = this.row(z - rad);
    const r1 = this.row(z + rad);
    const rad2 = rad * rad;
    let changed = false;
    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) {
        if (!this.inBounds(c, r)) continue;
        const cx = -this.extent + (c + 0.5) * this.cell;
        const cz = -this.extent + (r + 0.5) * this.cell;
        const dx = cx - x;
        const dz = cz - z;
        if (dx * dx + dz * dz <= rad2) {
          const i = this.idx(c, r);
          if (this.revealed[i] === 0) {
            this.revealed[i] = 1;
            changed = true;
          }
        }
      }
    }
    return changed;
  }

  snapshot(): number[] {
    const out: number[] = [];
    for (let i = 0; i < this.revealed.length; i++) if (this.revealed[i]) out.push(i);
    return out;
  }
  load(cells: number[] | undefined, fromExtent?: number): void {
    this.revealed.fill(0);
    if (!cells) return;
    if (fromExtent !== undefined && fromExtent !== this.extent) {
      // 10.9HQ — MIGRAÇÃO: índices do grid antigo → posição no mundo → célula deste grid
      const oldCols = Math.ceil((fromExtent * 2) / this.cell);
      for (const i of cells) {
        if (!(i >= 0)) continue;
        const c = i % oldCols, r = Math.floor(i / oldCols);
        const x = -fromExtent + (c + 0.5) * this.cell, z = -fromExtent + (r + 0.5) * this.cell;
        const nc = this.col(x), nr = this.row(z);
        if (this.inBounds(nc, nr)) this.revealed[this.idx(nc, nr)] = 1;
      }
      return;
    }
    for (const i of cells) if (i >= 0 && i < this.revealed.length) this.revealed[i] = 1;
  }
  reset(): void {
    this.revealed.fill(0);
  }
}
